import * as React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface SpotlightProps {
  data: string;
  author?: string;
  publishedAt?: string;
}

function Spotlight({ data, author, publishedAt }: SpotlightProps) {
  return (
    <div className="article-container">
      <div className="blog-navigation">
        <a href="/blog" className="back-to-blog">← Back to Blog</a>
      </div>
      <div className="content-wrapper">
        {(author || publishedAt) && (
          <div className="article-meta">
            {author && (
              <div className="author-info">
                <img
                  src={`https://ui-avatars.com/api/?name=${encodeURIComponent(author)}&background=random`}
                  alt={author}
                  className="author-avatar"
                />
                <span className="author-name">{author}</span>
              </div>
            )}
            {publishedAt && (
              <time className="publish-date">{publishedAt}</time>
            )}
          </div>
        )}
        <div className="article-content">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              h1: ({node, ...props}) => <h1 className="title" {...props} />,
              h2: ({node, ...props}) => <h2 className="section-title" {...props} />,
              h3: ({node, ...props}) => <h3 className="subsection-title" {...props} />,
              p: ({node, ...props}) => <p className="paragraph" {...props} />,
              ul: ({node, ...props}) => <ul className="medium-list" {...props} />,
              li: ({node, ...props}) => <li className="list-item" {...props} />,
            }}
          >
            {data}
          </ReactMarkdown>
        </div>
      </div>

      <style>
        {`
          .article-container {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
            color: rgb(41, 41, 41);
          }

          .blog-navigation {
            margin-bottom: 32px;
          }

          .back-to-blog {
            color: rgb(41, 41, 41);
            text-decoration: none;
            font-size: 16px;
            font-weight: 500;
            transition: color 0.2s ease;
          }

          .back-to-blog:hover {
            color: rgb(0, 0, 0);
            text-decoration: underline;
          }

          .article-meta {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 32px;
            padding-bottom: 24px;
            border-bottom: 1px solid rgba(230, 230, 230, 0.8);
          }

          .author-info {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .author-avatar {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            object-fit: cover;
          }

          .author-name {
            color: rgb(36, 36, 36);
            font-size: 16px;
            font-weight: 500;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
          }

          .publish-date {
            color: rgb(117, 117, 117);
            font-size: 14px;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Oxygen, Ubuntu, Cantarell, "Open Sans", "Helvetica Neue", sans-serif;
          }

          .content-wrapper {
            line-height: 1.8;
          }

          .title {
            font-size: clamp(28px, 5vw, 48px);
            font-weight: 700;
            margin-bottom: 32px;
            line-height: 1.2;
            letter-spacing: -0.02em;
            color: rgb(25, 25, 25);
            word-wrap: break-word;
            overflow-wrap: break-word;
          }

          .section-title {
            font-size: clamp(22px, 4vw, 34px);
            font-weight: 700;
            margin: 56px 0 28px;
            line-height: 1.3;
            letter-spacing: -0.015em;
            color: rgb(25, 25, 25);
            word-wrap: break-word;
            overflow-wrap: break-word;
          }

          .subsection-title {
            font-size: clamp(18px, 3vw, 26px);
            font-weight: 700;
            margin: 44px 0 22px;
            line-height: 1.3;
            letter-spacing: -0.012em;
            color: rgb(25, 25, 25);
            word-wrap: break-word;
            overflow-wrap: break-word;
          }

          .paragraph {
            font-size: 20px;
            margin-bottom: 32px;
            color: rgb(41, 41, 41);
            line-height: 1.8;
            font-weight: 400;
          }

          .medium-list {
            margin: 0 0 32px;
            padding-left: 40px;
            list-style-type: disc;
          }

          .list-item {
            font-size: 20px;
            margin-bottom: 16px;
            padding-left: 0;
            color: rgb(41, 41, 41);
            line-height: 1.8;
            font-weight: 400;
          }

          .list-item::before {
            display: none;
          }

          .list-item a {
            color: rgb(41, 41, 41);
            text-decoration: underline;
          }

          @media (max-width: 768px) {
            .article-container {
              padding: 16px;
            }

            .title {
              line-height: 1.25;
            }

            .section-title {
              line-height: 1.35;
              margin: 44px 0 24px;
            }

            .subsection-title {
              line-height: 1.35;
              margin: 36px 0 20px;
            }

            .paragraph,
            .list-item {
              font-size: 18px;
              line-height: 1.75;
              margin-bottom: 28px;
            }

            .list-item {
              margin-bottom: 14px;
            }

            .article-meta {
              flex-direction: column;
              align-items: flex-start;
              gap: 12px;
            }

            .author-avatar {
              width: 28px;
              height: 28px;
            }

            .author-name {
              font-size: 15px;
            }

            .publish-date {
              font-size: 13px;
            }
          }
        `}
      </style>
    </div>
  );
}

export default Spotlight;
